import { ChangePasswordDialog } from '@/components/features/auth/ChangePasswordDialog'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { canAccessManagement } from '@/domain/auth/auth'
import { Paths } from '@/routes/paths'
import { useNavigate } from '@tanstack/react-router'
import { Bell, KeyRound, LogOut, Menu, Moon, Sun, User } from 'lucide-react'
import { useState } from 'react'
import { useLogout } from '../../../hooks/api/useAuth'
import { useAppStore } from '../../../store/useAppStore'
import { useAuthStore } from '../../../store/useAuthStore'
import { Button } from '../../ui/button'

export function Header() {
  const { theme, setTheme, toggleMobileSidebar } = useAppStore()
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [showChangePassword, setShowChangePassword] = useState(false)

  const logoutType = user && canAccessManagement(user) ? 'management' : 'public'
  const logoutMutation = useLogout(logoutType, () => {
    // Navigate to appropriate login page after logout
    const redirectPath = logoutType === 'management' ? Paths.ManagementLogin : Paths.PublicLogin
    navigate({ to: `${redirectPath}` })
  })

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
  }

  const handleLogout = () => {
    logoutMutation.mutate()
  }

  return (
    <header className="h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-full items-center justify-between px-6">
        {/* Left side - mobile hamburger and title */}
        <div className="flex items-center gap-4">
          {/* Mobile hamburger menu - only visible on mobile */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleMobileSidebar}
            className="h-9 w-9 md:hidden"
          >
            <Menu className="h-4 w-4" />
          </Button>
          {/* <h2 className="text-lg font-semibold">Menú principal</h2> */}
        </div>

        {/* Right side */}
        <div className="flex items-center gap-2">
          {/* Theme toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            className="h-9 w-9"
          >
            {theme === 'light' ? (
              <Moon className="h-4 w-4" />
            ) : (
              <Sun className="h-4 w-4" />
            )}
          </Button>

          {/* Notifications */}
          <Button variant="ghost" size="icon" className="h-9 w-9">
            <Bell className="h-4 w-4" />
          </Button>

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-9 gap-2">
                <User className="h-4 w-4" />
                <span className="truncate">
                  {user?.fullName || user?.email}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium leading-none">
                    {/* {user?.name() || user?.email} */}
                    {user?.fullName || user?.email}
                  </p>
                  <p className="text-xs leading-none text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setShowChangePassword(true)}>
                <KeyRound className="mr-2 h-4 w-4" />
                <span>Cambiar Contraseña</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} disabled={logoutMutation.isPending}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Cerrar Sesión</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <ChangePasswordDialog open={showChangePassword} onOpenChange={setShowChangePassword} />
    </header>
  )
}
